/**
 * Valnexo CRM - Academy Module Management
 * ========================================
 * Page pour créer et gérer les modules (Admin Full uniquement)
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { academyAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
    ArrowLeft, Plus, Pencil, Trash2, GripVertical, Video, FileText,
    BookOpen, Award, Save, Eye, EyeOff, Zap, HardHat, Monitor,
    ChevronDown, ChevronUp, X, Check, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import ModuleResources from '@/components/academy/ModuleResources';

const CATEGORIES = [
    { id: 'crm_valnexo', name: 'CRM Valnexo', icon: Monitor },
    { id: 'easy_power', name: 'Courtage', icon: Zap },
    { id: 'chantier', name: 'Travaux d’optimisation', icon: HardHat },
    { id: 'general', name: 'Général', icon: BookOpen }
];

const CONTENT_TYPES = [
    { id: 'video', name: 'Vidéo', icon: Video },
    { id: 'document', name: 'Document', icon: FileText },
    { id: 'article', name: 'Article', icon: BookOpen }
];

// Dialog pour créer/éditer un module
const ModuleDialog = ({ open, onClose, module, onSave }) => {
    const [form, setForm] = useState({
        title: '',
        description: '',
        category: 'general',
        estimated_duration_minutes: 30,
        is_mandatory: true,
        restricted_to_staff: false,
        visible_to_parrain: false
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (module) {
            setForm({
                title: module.title || '',
                description: module.description || '',
                category: module.category || 'general',
                estimated_duration_minutes: module.estimated_duration_minutes || 30,
                is_mandatory: module.is_mandatory ?? true,
                restricted_to_staff: module.restricted_to_staff ?? false,
                visible_to_parrain: module.visible_to_parrain ?? false
            });
        } else {
            setForm({
                title: '',
                description: '',
                category: 'general',
                estimated_duration_minutes: 30,
                is_mandatory: true,
                restricted_to_staff: false,
                visible_to_parrain: false
            });
        }
    }, [module, open]);

    const handleSubmit = async () => {
        if (!form.title.trim()) {
            toast.error('Le titre est requis');
            return;
        }
        
        setSaving(true);
        try {
            if (module) {
                await academyAPI.updateModule(module.id, form);
                toast.success('Module mis à jour');
            } else {
                await academyAPI.createModule(form);
                toast.success('Module créé');
            }
            onSave();
            onClose();
        } catch (err) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{module ? 'Modifier le module' : 'Nouveau module'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label>Titre *</Label>
                        <Input
                            value={form.title}
                            onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
                            placeholder="Ex: Introduction au CRM"
                        />
                    </div>
                    <div>
                        <Label>Description</Label>
                        <Textarea
                            value={form.description}
                            onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                            placeholder="Description du module..."
                            rows={3}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>Catégorie</Label>
                            <Select 
                                value={form.category} 
                                onValueChange={(v) => setForm(p => ({ ...p, category: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map(cat => (
                                        <SelectItem key={cat.id} value={cat.id}>
                                            <div className="flex items-center gap-2">
                                                <cat.icon className="h-4 w-4" />
                                                {cat.name}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Durée estimée (min)</Label>
                            <Input
                                type="number"
                                value={form.estimated_duration_minutes}
                                onChange={(e) => setForm(p => ({ ...p, estimated_duration_minutes: parseInt(e.target.value) || 30 }))}
                                min={1}
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={form.is_mandatory}
                            onCheckedChange={(v) => setForm(p => ({ ...p, is_mandatory: v }))}
                        />
                        <Label>Module obligatoire</Label>
                    </div>
                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                        <Switch
                            checked={form.restricted_to_staff}
                            onCheckedChange={(v) => setForm(p => ({ ...p, restricted_to_staff: v }))}
                            data-testid="module-restricted-switch"
                        />
                        <div>
                            <Label className="text-amber-900">Réservé Admin + VP</Label>
                            <p className="text-xs text-amber-700">Caché pour les apporteurs et les autres rôles. Seuls les Admin et VP voient ce module.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-2 rounded-lg border border-purple-200 bg-purple-50 p-3">
                        <Switch
                            checked={form.visible_to_parrain}
                            onCheckedChange={(v) => setForm(p => ({ ...p, visible_to_parrain: v }))}
                            data-testid="module-parrain-switch"
                        />
                        <div>
                            <Label className="text-purple-900">Rendre visible aux Parrains (Réseau Parrainage)</Label>
                            <p className="text-xs text-purple-700">Ce module devient <strong>aussi visible par les parrains (tous niveaux)</strong> — sans être retiré aux autres rôles. Sans cette option, les parrains ne verront pas ce module.</p>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button onClick={handleSubmit} disabled={saving}>
                        <Save className="h-4 w-4 mr-2" />
                        {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// Dialog pour ajouter un contenu
const ContentDialog = ({ open, onClose, moduleId, content, onSave }) => {
    const [form, setForm] = useState({
        type: 'video',
        title: '',
        description: '',
        url: '',
        content_text: '',
        duration_minutes: 10,
        order: 0
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (content) {
            setForm({
                type: content.type || 'video',
                title: content.title || '',
                description: content.description || '',
                url: content.url || '',
                content_text: content.content_text || '',
                duration_minutes: content.duration_minutes || 10,
                order: content.order || 0
            });
        } else {
            setForm({
                type: 'video',
                title: '',
                description: '',
                url: '',
                content_text: '',
                duration_minutes: 10,
                order: 0
            });
        }
    }, [content, open]);

    const handleSubmit = async () => {
        if (!form.title.trim()) {
            toast.error('Le titre est requis');
            return;
        }
        
        setSaving(true);
        try {
            if (content) {
                await academyAPI.updateContent(moduleId, content.id, form);
                toast.success('Contenu mis à jour');
            } else {
                await academyAPI.addContent(moduleId, form);
                toast.success('Contenu ajouté');
            }
            onSave();
            onClose();
        } catch (err) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{content ? 'Modifier le contenu' : 'Nouveau contenu'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label>Type de contenu</Label>
                        <Select 
                            value={form.type} 
                            onValueChange={(v) => setForm(p => ({ ...p, type: v }))}
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {CONTENT_TYPES.map(type => (
                                    <SelectItem key={type.id} value={type.id}>
                                        <div className="flex items-center gap-2">
                                            <type.icon className="h-4 w-4" />
                                            {type.name}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Titre *</Label>
                        <Input
                            value={form.title}
                            onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
                            placeholder="Titre du contenu"
                        />
                    </div>
                    <div>
                        <Label>Description</Label>
                        <Textarea
                            value={form.description}
                            onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                            placeholder="Description..."
                            rows={2}
                        />
                    </div>
                    
                    {form.type === 'video' && (
                        <div>
                            <Label>URL de la vidéo (YouTube, Vimeo)</Label>
                            <Input
                                value={form.url}
                                onChange={(e) => setForm(p => ({ ...p, url: e.target.value }))}
                                placeholder="https://youtube.com/watch?v=..."
                            />
                        </div>
                    )}
                    
                    {form.type === 'document' && (
                        <div>
                            <Label>URL du document</Label>
                            <Input
                                value={form.url}
                                onChange={(e) => setForm(p => ({ ...p, url: e.target.value }))}
                                placeholder="https://..."
                            />
                        </div>
                    )}
                    
                    {form.type === 'article' && (
                        <div>
                            <Label>Contenu de l'article (HTML)</Label>
                            <Textarea
                                value={form.content_text}
                                onChange={(e) => setForm(p => ({ ...p, content_text: e.target.value }))}
                                placeholder="<p>Votre contenu...</p>"
                                rows={6}
                            />
                        </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>Durée estimée (min)</Label>
                            <Input
                                type="number"
                                value={form.duration_minutes}
                                onChange={(e) => setForm(p => ({ ...p, duration_minutes: parseInt(e.target.value) || 10 }))}
                                min={1}
                            />
                        </div>
                        <div>
                            <Label>Ordre</Label>
                            <Input
                                type="number"
                                value={form.order}
                                onChange={(e) => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))}
                                min={0}
                            />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button onClick={handleSubmit} disabled={saving}>
                        <Save className="h-4 w-4 mr-2" />
                        {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// Dialog pour créer un quiz
const QuizDialog = ({ open, onClose, moduleId, quiz, onSave }) => {
    const [questions, setQuestions] = useState([]);
    const [passingScore, setPassingScore] = useState(70);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (quiz) {
            setQuestions(quiz.questions || []);
            setPassingScore(quiz.passing_score || 70);
        } else {
            setQuestions([]);
            setPassingScore(70);
        }
    }, [quiz, open]);

    const addQuestion = () => {
        setQuestions(prev => [...prev, {
            id: `q_${Date.now()}`,
            question: '',
            options: ['', '', '', ''],
            correct_answer: 0,
            explanation: ''
        }]);
    };

    const updateQuestion = (index, field, value) => {
        setQuestions(prev => prev.map((q, i) => i === index ? { ...q, [field]: value } : q));
    };

    const updateOption = (qIndex, oIndex, value) => {
        setQuestions(prev => prev.map((q, i) => {
            if (i !== qIndex) return q;
            const newOptions = [...q.options];
            newOptions[oIndex] = value;
            return { ...q, options: newOptions };
        }));
    };

    const removeQuestion = (index) => {
        setQuestions(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        if (questions.length === 0) {
            toast.error('Ajoutez au moins une question');
            return;
        }
        
        // Valider les questions
        for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            if (!q.question.trim()) {
                toast.error(`Question ${i + 1}: Le texte est requis`);
                return;
            }
            if (q.options.some(o => !o.trim())) {
                toast.error(`Question ${i + 1}: Toutes les options doivent être remplies`);
                return;
            }
        }
        
        setSaving(true);
        try {
            await academyAPI.createQuiz(moduleId, {
                questions,
                passing_score: passingScore
            });
            toast.success('Quiz sauvegardé');
            onSave();
            onClose();
        } catch (err) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Gérer le quiz</DialogTitle>
                    <DialogDescription>
                        Créez des questions à choix multiples pour valider le module
                    </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                    <div>
                        <Label>Score minimum pour valider (%)</Label>
                        <Input
                            type="number"
                            value={passingScore}
                            onChange={(e) => setPassingScore(parseInt(e.target.value) || 70)}
                            min={0}
                            max={100}
                            className="w-32"
                        />
                    </div>
                    
                    <Separator />
                    
                    {questions.map((q, qIndex) => (
                        <Card key={q.id}>
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-sm">Question {qIndex + 1}</CardTitle>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeQuestion(qIndex)}
                                    >
                                        <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div>
                                    <Label>Question</Label>
                                    <Input
                                        value={q.question}
                                        onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                                        placeholder="Posez votre question..."
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Options (cochez la bonne réponse)</Label>
                                    {q.options.map((opt, oIndex) => (
                                        <div key={oIndex} className="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                name={`correct_${q.id}`}
                                                checked={q.correct_answer === oIndex}
                                                onChange={() => updateQuestion(qIndex, 'correct_answer', oIndex)}
                                                className="w-4 h-4"
                                            />
                                            <Input
                                                value={opt}
                                                onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                                                placeholder={`Option ${oIndex + 1}`}
                                                className="flex-1"
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div>
                                    <Label>Explication (optionnel)</Label>
                                    <Input
                                        value={q.explanation}
                                        onChange={(e) => updateQuestion(qIndex, 'explanation', e.target.value)}
                                        placeholder="Explication affichée après la réponse..."
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                    
                    <Button variant="outline" onClick={addQuestion} className="w-full">
                        <Plus className="h-4 w-4 mr-2" />
                        Ajouter une question
                    </Button>
                </div>
                
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button onClick={handleSubmit} disabled={saving}>
                        <Save className="h-4 w-4 mr-2" />
                        {saving ? 'Sauvegarde...' : 'Sauvegarder le quiz'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default function AcademyManage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [modules, setModules] = useState([]);
    const [expandedModule, setExpandedModule] = useState(null);
    
    // Dialogs
    const [moduleDialog, setModuleDialog] = useState({ open: false, module: null });
    const [contentDialog, setContentDialog] = useState({ open: false, moduleId: null, content: null });
    const [quizDialog, setQuizDialog] = useState({ open: false, moduleId: null, quiz: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, module: null });

    useEffect(() => {
        if (user?.role !== 'admin_full' && user?.is_admin !== true) {
            navigate('/academy');
            return;
        }
        loadModules();
    }, [user]);

    const loadModules = async () => {
        setLoading(true);
        try {
            const res = await academyAPI.getModules();
            setModules(res.data || []);
        } catch (err) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const loadModuleDetail = async (moduleId) => {
        try {
            const res = await academyAPI.getModule(moduleId);
            // Mettre à jour le module dans la liste
            setModules(prev => prev.map(m => m.id === moduleId ? res.data : m));
        } catch (err) {
            console.error(err);
        }
    };

    const handleTogglePublish = async (module) => {
        try {
            await academyAPI.updateModule(module.id, { is_published: !module.is_published });
            toast.success(module.is_published ? 'Module dépublié' : 'Module publié');
            loadModules();
        } catch (err) {
            toast.error('Erreur');
        }
    };

    const handleDeleteModule = async () => {
        if (!deleteDialog.module) return;
        try {
            await academyAPI.deleteModule(deleteDialog.module.id);
            toast.success('Module supprimé');
            setDeleteDialog({ open: false, module: null });
            loadModules();
        } catch (err) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleDeleteContent = async (moduleId, contentId) => {
        try {
            await academyAPI.deleteContent(moduleId, contentId);
            toast.success('Contenu supprimé');
            loadModuleDetail(moduleId);
        } catch (err) {
            toast.error('Erreur');
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-4">
                    <Skeleton className="h-10 w-64" />
                    <Skeleton className="h-64" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="academy-manage-page">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/academy')}>
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold">Gestion des modules</h1>
                        <p className="text-muted-foreground">
                            Créez et gérez les modules de formation
                        </p>
                    </div>
                    <Button onClick={() => setModuleDialog({ open: true, module: null })}>
                        <Plus className="h-4 w-4 mr-2" />
                        Nouveau module
                    </Button>
                </div>

                {/* Liste des modules */}
                {modules.length === 0 ? (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                            <p className="text-muted-foreground mb-4">Aucun module créé</p>
                            <Button onClick={() => setModuleDialog({ open: true, module: null })}>
                                <Plus className="h-4 w-4 mr-2" />
                                Créer le premier module
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {modules.map((module) => {
                            const isExpanded = expandedModule === module.id;
                            const CategoryIcon = CATEGORIES.find(c => c.id === module.category)?.icon || BookOpen;
                            
                            return (
                                <Card key={module.id}>
                                    <CardHeader 
                                        className="cursor-pointer"
                                        onClick={() => {
                                            if (isExpanded) {
                                                setExpandedModule(null);
                                            } else {
                                                setExpandedModule(module.id);
                                                loadModuleDetail(module.id);
                                            }
                                        }}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-slate-100 rounded-lg">
                                                    <CategoryIcon className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    <CardTitle className="text-lg">{module.title}</CardTitle>
                                                    <CardDescription>{module.description}</CardDescription>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Badge variant={module.is_published ? "default" : "secondary"}>
                                                    {module.is_published ? 'Publié' : 'Brouillon'}
                                                </Badge>
                                                <Badge variant="outline">
                                                    {module.contents_count || 0} contenus
                                                </Badge>
                                                {module.has_quiz && (
                                                    <Badge variant="outline" className="bg-yellow-50">
                                                        <Award className="h-3 w-3 mr-1" />
                                                        Quiz
                                                    </Badge>
                                                )}
                                                {isExpanded ? (
                                                    <ChevronUp className="h-5 w-5" />
                                                ) : (
                                                    <ChevronDown className="h-5 w-5" />
                                                )}
                                            </div>
                                        </div>
                                    </CardHeader>
                                    
                                    {isExpanded && (
                                        <CardContent className="border-t pt-4">
                                            {/* Actions du module */}
                                            <div className="flex gap-2 mb-4">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setModuleDialog({ open: true, module });
                                                    }}
                                                >
                                                    <Pencil className="h-4 w-4 mr-1" />
                                                    Modifier
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleTogglePublish(module);
                                                    }}
                                                >
                                                    {module.is_published ? (
                                                        <>
                                                            <EyeOff className="h-4 w-4 mr-1" />
                                                            Dépublier
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Eye className="h-4 w-4 mr-1" />
                                                            Publier
                                                        </>
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setQuizDialog({ open: true, moduleId: module.id, quiz: module.quiz });
                                                    }}
                                                >
                                                    <Award className="h-4 w-4 mr-1" />
                                                    {module.has_quiz ? 'Modifier Quiz' : 'Ajouter Quiz'}
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="text-destructive"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDeleteDialog({ open: true, module });
                                                    }}
                                                >
                                                    <Trash2 className="h-4 w-4 mr-1" />
                                                    Supprimer
                                                </Button>
                                            </div>
                                            
                                            <Separator className="my-4" />
                                            
                                            {/* Liste des contenus */}
                                            <div className="space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <h4 className="font-medium">Contenus</h4>
                                                    <Button
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setContentDialog({ open: true, moduleId: module.id, content: null });
                                                        }}
                                                    >
                                                        <Plus className="h-4 w-4 mr-1" />
                                                        Ajouter
                                                    </Button>
                                                </div>
                                                
                                                {module.contents?.length > 0 ? (
                                                    <div className="space-y-1">
                                                        {module.contents.map((content, index) => {
                                                            const ContentIcon = CONTENT_TYPES.find(t => t.id === content.type)?.icon || BookOpen;
                                                            return (
                                                                <div 
                                                                    key={content.id}
                                                                    className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
                                                                >
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-xs text-muted-foreground w-6">
                                                                            {index + 1}.
                                                                        </span>
                                                                        <ContentIcon className="h-4 w-4 text-muted-foreground" />
                                                                        <span className="text-sm">{content.title}</span>
                                                                    </div>
                                                                    <div className="flex gap-1">
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="h-8 w-8"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setContentDialog({ open: true, moduleId: module.id, content });
                                                                            }}
                                                                        >
                                                                            <Pencil className="h-3 w-3" />
                                                                        </Button>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="h-8 w-8 text-destructive"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleDeleteContent(module.id, content.id);
                                                                            }}
                                                                        >
                                                                            <Trash2 className="h-3 w-3" />
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <p className="text-sm text-muted-foreground text-center py-4">
                                                        Aucun contenu - Ajoutez des vidéos, documents ou articles
                                                    </p>
                                                )}
                                            </div>

                                            <Separator className="my-4" />

                                            {/* Trames & supports (drag & drop admin) */}
                                            <ModuleResources moduleId={module.id} canManage={true} />
                                        </CardContent>
                                    )}
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Dialogs */}
            <ModuleDialog
                open={moduleDialog.open}
                module={moduleDialog.module}
                onClose={() => setModuleDialog({ open: false, module: null })}
                onSave={loadModules}
            />
            
            <ContentDialog
                open={contentDialog.open}
                moduleId={contentDialog.moduleId}
                content={contentDialog.content}
                onClose={() => setContentDialog({ open: false, moduleId: null, content: null })}
                onSave={() => {
                    loadModules();
                    if (contentDialog.moduleId) loadModuleDetail(contentDialog.moduleId);
                }}
            />
            
            <QuizDialog
                open={quizDialog.open}
                moduleId={quizDialog.moduleId}
                quiz={quizDialog.quiz}
                onClose={() => setQuizDialog({ open: false, moduleId: null, quiz: null })}
                onSave={() => {
                    loadModules();
                    if (quizDialog.moduleId) loadModuleDetail(quizDialog.moduleId);
                }}
            />
            
            {/* Delete confirmation */}
            <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, module: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Supprimer le module ?</DialogTitle>
                        <DialogDescription>
                            Cette action supprimera définitivement le module "{deleteDialog.module?.title}" ainsi que tous ses contenus et les progressions des utilisateurs.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialog({ open: false, module: null })}>
                            Annuler
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteModule}>
                            <Trash2 className="h-4 w-4 mr-2" />
                            Supprimer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
